import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, HelpCircle, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import StepShell from "./StepShell";

/**
 * Step 5 — Rebates and incentives.
 *
 * Each rebate card renders:
 *   • Name + amount
 *   • Three actions: Apply / Not sure / Skip
 *   • A "Click here to see if you qualify" toggle that EXPANDS IN-CARD
 *     with eligibility copy (never opens underneath the page).
 *
 * The qualification copy is intentionally written as "may qualify" — we
 * never promise eligibility. Final eligibility is confirmed by the dealer.
 *
 * Strict guardrails:
 *   • No math, no APR overrides, no rebate-stack logic touched here. We
 *     read /api/incentives/vehicle/{vin} and forward selected ids back into
 *     the wizard state. The math layer (PaymentCalculator) consumes the
 *     resulting total_savings_estimated separately.
 */

// Helper text per rebate "kind" / canonical id — uses "may apply" language.
const ELIGIBILITY_COPY = {
  bonus_cash:
    "This offer is available to everyone when it applies to this vehicle.",
  gmf_bonus_cash:
    "This offer is available to everyone when it applies to this vehicle.",
  customer_cash:
    "This offer is available to everyone when it applies to this vehicle.",
  first_responder:
    "This may apply to eligible firefighters, police officers, EMTs, paramedics, 911 dispatchers, and other qualifying first responder roles.",
  military:
    "This may apply to eligible active duty, reserves, veterans, retirees, and qualifying military household members depending on GM program rules.",
  college:
    "This may apply to eligible current students, recent graduates, or qualifying college program customers depending on GM rules.",
  supplier:
    "This may apply to customers who work for an eligible GM Supplier company and can provide a valid authorization code.",
  employee:
    "This may apply to eligible GM employees, retirees, and qualifying family members with a valid authorization code.",
  costco:
    "This may apply to eligible Costco members when an active Costco Auto Program incentive is available.",
};

const fmt = (n) =>
  n === null || n === undefined || isNaN(n) ? "—" : `$${Number(n).toLocaleString()}`;

function eligibilityCopyFor(offer) {
  if (!offer) return "Eligibility depends on current GM program rules. Scott will confirm with you.";
  const id = String(offer.incentive_id || "").toLowerCase();
  if (ELIGIBILITY_COPY[id]) return ELIGIBILITY_COPY[id];
  const name = String(offer.name || "").toLowerCase();
  if (/first.?responder/.test(name)) return ELIGIBILITY_COPY.first_responder;
  if (/military|veteran/.test(name)) return ELIGIBILITY_COPY.military;
  if (/college|grad|student/.test(name)) return ELIGIBILITY_COPY.college;
  if (/supplier/.test(name)) return ELIGIBILITY_COPY.supplier;
  if (/employee/.test(name)) return ELIGIBILITY_COPY.employee;
  if (/costco/.test(name)) return ELIGIBILITY_COPY.costco;
  if (/bonus|cash|consumer/.test(name)) return ELIGIBILITY_COPY.bonus_cash;
  return "Eligibility depends on current GM program rules. Scott will confirm with you.";
}

export default function RebatesStep({ byod, vin, vehicle, onContinue, onBack, firstName }) {
  const isNew = (vehicle?.status || "").toLowerCase() === "new";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // incentive_id → bool

  const reb = byod.state.rebates;

  useEffect(() => {
    if (!vin || !isNew) return undefined;
    let on = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data: d } = await api.get(`/incentives/vehicle/${vin}`);
        if (on) setData(d);
      } catch (e) {
        if (on) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [vin, isNew]);

  const selectableOffers = useMemo(() => data?.selectable_offers || [], [data]);
  const includedOffers = data?.included_offers || [];

  const totalSavings = useMemo(
    () => (reb.selected || []).reduce((s, o) => s + (Number(o.amount) || 0), 0),
    [reb.selected]
  );

  // Persist computed total + acknowledged flag back to wizard state
  useEffect(() => {
    byod.update("rebates", { total_savings_estimated: totalSavings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalSavings]);

  const status = (offerId) => {
    if ((reb.selected || []).some((o) => o.incentive_id === offerId)) return "apply";
    if ((reb.not_sure || []).some((o) => o.incentive_id === offerId)) return "not_sure";
    if ((reb.skipped || []).some((o) => o.incentive_id === offerId)) return "skip";
    return null;
  };

  // Compatibility-aware Apply. Mirrors RebateSelector's prior behaviour:
  //   • Toggle off if already applied.
  //   • Otherwise POST /incentives/check with all currently-applied IDs +
  //     the Special-APR ID from rate step (if any) — if compat fails,
  //     open the in-page conflict modal so the customer can either
  //     replace or cancel.
  const [conflict, setConflict] = useState(null); // { candidate, reason, conflicts_with:[{id,name}] }

  const applyOrConflict = async (offer) => {
    setError("");
    if (status(offer.incentive_id) === "apply") {
      // toggle off
      const updated = {
        ...reb,
        selected: (reb.selected || []).filter((o) => o.incentive_id !== offer.incentive_id),
      };
      byod.replaceSection("rebates", updated);
      return;
    }
    const aprId = byod.state.rate?.special_apr_id || null;
    const selectedIds = [
      ...(reb.selected || []).map((s) => s.incentive_id),
      ...(aprId ? [aprId] : []),
    ];
    try {
      const { data: chk } = await api.post("/incentives/check", {
        candidate_id: offer.incentive_id,
        selected_ids: selectedIds,
        deal_type: "finance",
      });
      // v6.5.7 — Never let Bonus Cash variants be flagged "already included"
      const FORCE_OK_IDS = new Set(["gmf_bonus_cash", "customer_cash", "bonus_cash"]);
      const isForcedOk =
        FORCE_OK_IDS.has(String(offer.incentive_id || "").toLowerCase()) &&
        !chk.ok &&
        /already.+included.+advertised|included in price/i.test(chk.reason || "");
      if (chk.ok || isForcedOk) {
        // remove from any other bucket and add to selected
        setStatus(offer, "apply");
      } else {
        const conflictsDetail = (chk.conflicts_with || []).map((cid) => {
          if (cid === aprId) {
            return { id: cid, name: byod.state.rate?.special_apr_name || "Special APR", isApr: true };
          }
          const m = (reb.selected || []).find((s) => s.incentive_id === cid);
          return { id: cid, name: m?.name || cid };
        });
        setConflict({ candidate: offer, reason: chk.reason, conflicts_with: conflictsDetail });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const replaceWith = () => {
    if (!conflict) return;
    const removeIds = conflict.conflicts_with.map((c) => c.id);
    // Drop conflicting rebates
    const newSelected = (reb.selected || []).filter((s) => !removeIds.includes(s.incentive_id));
    // If a Special APR is in the conflict list, clear it too
    const removeApr = conflict.conflicts_with.some((c) => c.isApr);
    if (removeApr) {
      byod.update("rate", { special_apr_id: null, special_apr_pct: null, special_apr_term: null, special_apr_name: null });
    }
    byod.replaceSection("rebates", {
      ...reb,
      selected: [...newSelected, conflict.candidate],
    });
    setConflict(null);
  };

  const setStatus = (offer, next) => {
    const id = offer.incentive_id;
    const without = {
      selected: (reb.selected || []).filter((o) => o.incentive_id !== id),
      not_sure: (reb.not_sure || []).filter((o) => o.incentive_id !== id),
      skipped:  (reb.skipped  || []).filter((o) => o.incentive_id !== id),
    };
    const updated = { ...reb, ...without };
    if (next === "apply") updated.selected = [...without.selected, offer];
    else if (next === "not_sure") updated.not_sure = [...without.not_sure, offer];
    else if (next === "skip") updated.skipped = [...without.skipped, offer];
    byod.replaceSection("rebates", updated);
  };

  const acknowledged = !!reb.acknowledged;
  const setAck = (v) => byod.update("rebates", { acknowledged: v });

  const continueDisabled = (reb.selected?.length || 0) > 0 && !acknowledged;

  if (!isNew) {
    return (
      <StepShell
        title={firstName ? `${firstName}, used-vehicle deals don't have GM rebates.` : "Used-vehicle deals don't have GM rebates."}
        subtitle="That's normal — used vehicles use their advertised price directly. Let's keep moving."
        onBack={onBack}
        onContinue={onContinue}
        testIdPrefix="byod-rebates-step"
      >
        <div className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-5">
          <p className="text-sm text-[var(--ssc-ink)]">
            GM rebates and offers only apply to new Chevrolet vehicles. We&rsquo;ll skip
            this step on used vehicles.
          </p>
        </div>
      </StepShell>
    );
  }

  return (
    <StepShell
      title={firstName ? `${firstName}, here are the rebates that may apply.` : "Here are the rebates that may apply."}
      subtitle="Pick the ones you want to include. Not sure? That's fine — Scott will confirm eligibility before anything is locked in."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={continueDisabled}
      testIdPrefix="byod-rebates-step"
    >
      {loading && (
        <div className="rounded-2xl border border-[var(--ssc-line)] bg-white p-5 text-sm text-[var(--ssc-ink-2)]" data-testid="byod-rebates-loading">
          Loading available offers...
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2" data-testid="byod-rebates-error">{error}</div>
      )}

      {includedOffers.length > 0 && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 mb-4">
          <div className="font-bold text-emerald-900 flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Already Included in Scott&rsquo;s Price
          </div>
          <ul className="mt-1 ml-5 list-disc text-emerald-900 text-sm">
            {includedOffers.map((o, i) => (
              <li key={i}>{o.name}{o.amount ? ` · ${fmt(o.amount)}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {selectableOffers.length === 0 && !loading && !error && (
        <div className="rounded-2xl border border-[var(--ssc-line)] bg-white p-5 text-sm text-[var(--ssc-ink-2)]" data-testid="byod-rebates-empty">
          No additional GM offers available on this VIN right now.
        </div>
      )}

      <ul className="space-y-3" data-testid="byod-rebates-list">
        {selectableOffers.map((o) => {
          const cur = status(o.incentive_id);
          const isOpen = !!expanded[o.incentive_id];
          return (
            <li
              key={o.incentive_id || o.name}
              className={`rounded-2xl border-2 transition ${cur === "apply" ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.04]" : "border-[var(--ssc-line)] bg-white"}`}
              data-testid={`byod-rebate-card-${o.incentive_id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold text-[var(--ssc-navy)] leading-tight">{o.name}</div>
                    {o.amount > 0 && (
                      <div className="text-2xl font-black text-[var(--ssc-navy)] mt-0.5" data-testid={`byod-rebate-amount-${o.incentive_id}`}>
                        {fmt(o.amount)}
                      </div>
                    )}
                  </div>
                  {cur && (
                    <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full ${
                      cur === "apply" ? "bg-[var(--ssc-blue)] text-white"
                      : cur === "not_sure" ? "bg-amber-100 text-amber-800"
                      : "bg-[var(--ssc-silver-2)] text-[var(--ssc-ink-2)]"
                    }`}>
                      {cur === "apply" ? "Applied" : cur === "not_sure" ? "Not sure" : "Skipped"}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => applyOrConflict(o)}
                    className={`ssc-btn !py-2 !text-xs ${cur === "apply" ? "ssc-btn-primary" : "ssc-btn-outline"}`}
                    data-testid={`byod-rebate-apply-${o.incentive_id}`}
                  >
                    {cur === "apply" ? "Applied" : "Apply"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(o, "not_sure")}
                    className={`ssc-btn !py-2 !text-xs ${cur === "not_sure" ? "ssc-btn-secondary" : "ssc-btn-outline"}`}
                    data-testid={`byod-rebate-notsure-${o.incentive_id}`}
                  >
                    Not sure
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(o, "skip")}
                    className={`ssc-btn !py-2 !text-xs ${cur === "skip" ? "ssc-btn-secondary" : "ssc-btn-outline"}`}
                    data-testid={`byod-rebate-skip-${o.incentive_id}`}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpanded((m) => ({ ...m, [o.incentive_id]: !m[o.incentive_id] }))}
                    className={`ssc-btn !py-2 !text-xs ${isOpen ? "ssc-btn-navy" : "ssc-btn-outline"} border-[var(--ssc-blue)] text-[var(--ssc-blue)]`}
                    data-testid={`byod-rebate-qualify-${o.incentive_id}`}
                    aria-expanded={isOpen}
                  >
                    <HelpCircle size={13} /> {isOpen ? "Hide eligibility" : "See If I Qualify"}
                  </button>
                </div>

                {/* IN-CARD eligibility detail — stays open until manually closed */}
                {isOpen && (
                  <div
                    className="mt-3 pt-3 border-t border-[var(--ssc-line)] text-sm text-[var(--ssc-ink)] bg-[var(--ssc-paper)] rounded-lg p-3"
                    data-testid={`byod-rebate-qualify-detail-${o.incentive_id}`}
                  >
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-1.5">
                      Who may qualify
                    </div>
                    <p className="leading-relaxed">{eligibilityCopyFor(o)}</p>
                    <p className="text-xs text-[var(--ssc-ink-2)] mt-2 leading-snug">
                      Eligibility is confirmed by Byers Chevrolet — final approval depends on
                      proof of qualification and current GM program rules.
                    </p>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {totalSavings > 0 && (
        <div className="mt-5 rounded-2xl border-2 border-[var(--ssc-navy)] bg-[var(--ssc-paper)] p-4" data-testid="byod-rebates-total">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                Potential additional savings
              </div>
              <div className="text-2xl font-black text-[var(--ssc-navy)]">{fmt(totalSavings)}</div>
            </div>
            <label className="flex items-start gap-2 text-sm max-w-md">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5"
                data-testid="byod-rebates-acknowledge"
              />
              <span className="text-[var(--ssc-ink-2)]">
                I understand selected offers are estimates only and final eligibility must be confirmed with Byers Chevrolet.
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Conflict modal — restored from RebateSelector v5.5 */}
      {conflict && (
        <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-3" data-testid="byod-rebate-conflict-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-extrabold text-[var(--ssc-navy)]">Offers can&rsquo;t be combined</h3>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-1.5">
                  <strong>{conflict.candidate.name}</strong> can&rsquo;t be combined with what&rsquo;s currently selected.
                  {conflict.reason ? <> {conflict.reason}.</> : null}
                </p>
                <div className="mt-3">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Conflicts with</div>
                  <ul className="mt-1 ml-5 list-disc text-sm text-[var(--ssc-ink)]">
                    {conflict.conflicts_with.map((c) => (
                      <li key={c.id}>{c.name}{c.isApr ? " (Special APR)" : ""}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConflict(null)}
                className="ssc-btn ssc-btn-outline"
                data-testid="byod-rebate-conflict-cancel"
              >
                Keep current
              </button>
              <button
                type="button"
                onClick={replaceWith}
                className="ssc-btn ssc-btn-primary"
                data-testid="byod-rebate-conflict-replace"
              >
                Use {conflict.candidate.name} instead
              </button>
            </div>
          </div>
        </div>
      )}
    </StepShell>
  );
}
